import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo, useEffect } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import { createPatientAppointment, reschedulePatientAppointment } from '@utils/api/endpoints';
import { GuidedBookingErrorCode } from '@/components/SharedTypes';

// Define the step types for guided booking
export type GuidedBookingStep = 
  | 'appointmentSelection' 
  | 'bookingConfirmation' 
  | 'complete';

// JWT payload interface
interface GuidedBookingJWTPayload {
  sub: string;
  aud: string;
  exp: number;
  patientId: string;
  serviceId: string;
  language: string;
  operatorIds?: string[];
  startDate?: string;
  endDate?: string;
  mode?: 'create' | 'reschedule';
  existingAppointmentId?: string;
}

// Define the guided booking data structure - only essential appointment data
export interface GuidedBookingData {
  // Only the essential data needed to create an appointment
  scheduledAt: string;
  operatorId: string;
}

interface GuidedBookingContextType {
  // Current step management
  currentStep: GuidedBookingStep;
  visitedSteps: GuidedBookingStep[];
  canGoBack: boolean;

  // Submission state
  isSubmitting: boolean;
  submissionErrorCode: GuidedBookingErrorCode | null;

  // Token management
  token: string | null;
  tokenData: GuidedBookingJWTPayload | null;
  isTokenValid: boolean;
  isRescheduleMode: boolean;

  // Navigation functions
  goToStep: (step: GuidedBookingStep) => void;
  goBack: () => void;
  goForward: (step: GuidedBookingStep) => void;
  restart: () => void;

  // Submission functions
  submitAppointment: () => Promise<void>;
  retrySubmission: () => Promise<void>;

  // Data management
  bookingData: GuidedBookingData;
  updateBookingData: (data: Partial<GuidedBookingData>) => void;

  // Utility functions
  isCurrentStep: (step: GuidedBookingStep) => boolean;
  getStepTitle: (step: GuidedBookingStep) => string;
}

const initialGuidedBookingData: GuidedBookingData = {
  scheduledAt: '',
  operatorId: '',
};

const GuidedBookingContext = createContext<GuidedBookingContextType | null>(null);

interface GuidedBookingProviderProps {
  children: ReactNode;
}

export const GuidedBookingProvider: React.FC<GuidedBookingProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const search = useSearch({ strict: false }) as { token?: string };

  const [visitedSteps, setVisitedSteps] = useState<GuidedBookingStep[]>(['appointmentSelection']);
  const [bookingData, setBookingData] = useState<GuidedBookingData>(initialGuidedBookingData);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionErrorCode, setSubmissionErrorCode] = useState<GuidedBookingErrorCode | null>(null);

  // Get token from search params
  const token = search?.token || null;

  // Parse and validate JWT token using useMemo
  const tokenData = useMemo(() => {
    if (!token) return null;
    
    try {
      // Decode JWT token using jwt-decode package
      const payload = jwtDecode<GuidedBookingJWTPayload>(token);
      
      console.log('Decoded JWT payload:', payload); // Debug log
      
      // Validate required fields
      if (!payload.patientId || !payload.serviceId) {
        console.error('Invalid booking token: missing required patient or service information');
        return null;
      }
      
      return payload;
    } catch (error) {
      console.error('JWT decode error:', error); // Debug log
      return null;
    }
  }, [token]);

  // Check if token is valid (not expired)
  const isTokenValid = useMemo(() => {
    if (!tokenData || !tokenData.exp) return false;
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const isValid = tokenData.exp > currentTime;
    if (!isValid) {
      console.error('Token has expired');
    }
    return isValid;
  }, [tokenData]);

  // Set language when token data changes
  useEffect(() => {
    if (tokenData?.language && tokenData.language !== i18n.language) {
      i18n.changeLanguage(tokenData.language);
    }
  }, [tokenData, i18n]);


  const currentStep = visitedSteps[visitedSteps.length - 1];
  const canGoBack = visitedSteps.length > 1 && currentStep !== 'complete';

  const goToStep = useCallback((step: GuidedBookingStep) => {
    setVisitedSteps(prev => [...prev, step]);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setVisitedSteps(prev => {
      if (prev.length > 1) {
        const newSteps = prev.slice(0, -1);
        const previousStep = newSteps[newSteps.length - 1];
        
        // Clear scheduledAt when going back to appointment selection from booking confirmation
        if (previousStep === 'appointmentSelection') {
          setBookingData(current => ({
            ...current,
            scheduledAt: '',
            operatorId: ''
          }));
        }
        
        return newSteps;
      }
      return prev;
    });
  }, []);

  // Check if this is a reschedule operation
  const isRescheduleMode = useMemo(() => {
    return tokenData?.mode === 'reschedule' && !!tokenData?.existingAppointmentId;
  }, [tokenData]);

  // Centralized submission method for internal booking
  const submitAppointment = useCallback(async () => {
    if (!token || !tokenData) {
      setSubmissionErrorCode(GuidedBookingErrorCode.NO_TOKEN);
      goToStep('complete');
      return;
    }

    setIsSubmitting(true);
    setSubmissionErrorCode(null);

    try {
      // Submit the appointment using the appropriate endpoint based on mode
      if (isRescheduleMode && tokenData.existingAppointmentId) {
        await reschedulePatientAppointment(
          tokenData.patientId,
          tokenData.existingAppointmentId,
          bookingData,
          token
        );
      } else {
        await createPatientAppointment(tokenData.patientId, bookingData, token);
      }

      // Success - navigate to completion
      goToStep('complete');
    } catch (err: any) {
      Sentry.captureException(err);

      // Handle specific error cases based on status code
      if (err?.status === 409) {
        setSubmissionErrorCode(GuidedBookingErrorCode.TOKEN_ALREADY_USED);
      } else if (err?.status === 422) {
        setSubmissionErrorCode(GuidedBookingErrorCode.VALIDATION_ERROR);
      } else {
        setSubmissionErrorCode(GuidedBookingErrorCode.GENERAL_ERROR);
      }

      // Still navigate to complete step to show error state
      goToStep('complete');
    } finally {
      setIsSubmitting(false);
    }
  }, [bookingData, goToStep, isRescheduleMode, token, tokenData]);

  // Retry submission method
  const retrySubmission = useCallback(async () => {
    await submitAppointment();
  }, [submitAppointment]);

  const goForward = useCallback(async (step: GuidedBookingStep) => {
    // For normal navigation, just go to the step
    goToStep(step);
  }, [goToStep]);

  const restart = useCallback(() => {
    setVisitedSteps(['appointmentSelection']);
    setBookingData(initialGuidedBookingData);
    setIsSubmitting(false);
    setSubmissionErrorCode(null);
  }, []);

  const updateBookingData = useCallback((data: Partial<GuidedBookingData>) => {
    setBookingData(prev => ({ ...prev, ...data }));
  }, []);

  const isCurrentStep = useCallback((step: GuidedBookingStep) => {
    return currentStep === step;
  }, [currentStep]);

  const getStepTitle = useCallback((step: GuidedBookingStep) => {
    const stepTitles = {
      appointmentSelection: t('steps.appointmentSelection'),
      bookingConfirmation: t('steps.guidedBookingConfirmation'),
      complete: t('steps.complete')
    };
    return stepTitles[step] || '';
  }, [t]);

  const contextValue: GuidedBookingContextType = {
    currentStep,
    visitedSteps,
    canGoBack,
    isSubmitting,
    submissionErrorCode,
    token,
    tokenData,
    isTokenValid,
    isRescheduleMode,
    goToStep,
    goBack,
    goForward,
    restart,
    submitAppointment,
    retrySubmission,
    bookingData,
    updateBookingData,
    isCurrentStep,
    getStepTitle
  };

  return (
    <GuidedBookingContext.Provider value={contextValue}>
      {children}
    </GuidedBookingContext.Provider>
  );
};

export const useGuidedBookingContext = () => {
  const context = useContext(GuidedBookingContext);
  if (!context) {
    throw new Error('useGuidedBookingContext must be used within a GuidedBookingProvider');
  }
  return context;
};